import { t } from '@if/core/i18n'
import DeleteIcon from '@mui/icons-material/Delete'
import { Autocomplete, Button, Card, IconButton, List, ListItem, ListItemText, Stack, TextField } from '@mui/material'
import { Person } from '@src/types/Person'
import { useMemo, useState } from 'react'
import { ArrayPath, Control, FieldArray, FieldArrayPath, FieldValues, useFieldArray } from 'react-hook-form'

interface PersonListInputProps<TFieldValues extends FieldValues, TName extends FieldArrayPath<TFieldValues>> {
  control: Control<TFieldValues>
  name: TName
  persons: Person[]
  onAdd: (persons: Person[]) => void
}

const PersonListInput = <TName extends FieldArrayPath<TFieldValues>, TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  persons,
  onAdd,
}: PersonListInputProps<TFieldValues, TName>) => {
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null)
  const [personOptions, setPersonOptions] = useState<Person[]>(persons)

  const handlePersonChange = (_event: React.SyntheticEvent, value: Person | null) => {
    setSelectedPerson(value)
  }
  const { fields, remove, append } = useFieldArray({
    control,
    name,
    keyName: 'fieldId',
  })
  const handleAdd = () => {
    if (selectedPerson) {
      append({
        ...selectedPerson,
      } as FieldArray<TFieldValues, ArrayPath<TFieldValues>>)
      setSelectedPerson(null)
      setPersonOptions(personOptions.filter((person) => person.fullName !== selectedPerson?.fullName))
    }
  }
  const handleAddAll = () => {
    onAdd(persons)
    setSelectedPerson(null)
    setPersonOptions([])
  }

  const values = useMemo(
    () =>
      fields.map((field) => {
        const personData = field as unknown as Person
        return {
          id: field.fieldId,
          personId: personData.id,
          fullName: personData.fullName,
          email: personData.email,
          startDate: personData.startDate,
          endDate: personData.endDate,
        }
      }),
    [fields],
  )

  const data = useMemo(
    () =>
      values.map((field, index) => (
        <ListItem
          key={field.id}
          secondaryAction={
            <IconButton
              edge="end"
              aria-label="delete"
              onClick={() => {
                remove(index)
                setPersonOptions(personOptions.concat(field as unknown as Person))
              }}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          }
        >
          <ListItemText primary={field.fullName} secondary={field.email} />
        </ListItem>
      )),
    [personOptions, remove, values],
  )

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Autocomplete
          options={personOptions}
          getOptionLabel={(option) => option.fullName || ''}
          isOptionEqualToValue={(option, value) => option.fullName === value.fullName}
          filterSelectedOptions
          renderInput={(params) => <TextField {...params} label={t('approvedRemoteExceptions.personName')} />}
          onChange={handlePersonChange}
          fullWidth
          value={selectedPerson}
        />
        <Button variant="contained" color="primary" onClick={handleAdd} disabled={!selectedPerson}>
          {t('common.add')}
        </Button>
        <Button variant="contained" color="primary" onClick={handleAddAll} style={{ flexShrink: 0 }}>
          {t('common.addAll')}
        </Button>
      </Stack>
      <Card variant="outlined" sx={{ maxHeight: 100, overflow: 'auto', my: 2 }}>
        <List dense>
          {data.length === 0 ? (
            <ListItem>
              <ListItemText primary="Empty" />
            </ListItem>
          ) : (
            data
          )}
        </List>
      </Card>
    </>
  )
}

export default PersonListInput
